import { VideoSlugDocument } from 'api';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

const apolloClient = initializeApollo();
const log = createLogger('getTalkSlug');

/**
 * Type for enriched video data from GraphQL
 */
export interface EnrichedVideoData {
  id: string;
  slug: string;
  title?: string;
  description?: string;
  presenterDisplayName?: string;
  duration?: number;
  viewedCount?: number;
  publishedAt?: string;
  topics?: {
    nodes: Array<{ name: string }>;
  };
  primaryImageSet?: Array<{
    url: string;
    aspectRatioName: string;
  }>;
}

/**
 * Cache to store ID to enriched video data mappings to avoid repeated GraphQL calls
 */
const videoDataCache = new Map<string, EnrichedVideoData>();

/**
 * Gets enriched video data from GraphQL using the talk ID from Sailthru.
 *
 * @param talkId - The talk ID returned from Sailthru
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<EnrichedVideoData | null> - The enriched video data or null if not found
 *
 * @example
 * ```ts
 * const talkId = '12345'; // ID from Sailthru
 * const videoData = await getEnrichedVideoData(talkId);
 * const talkUrl = `/talks/${videoData?.slug}`;
 * ```
 */
export const getEnrichedVideoData = async (
  talkId: string | number,
  language = 'en'
): Promise<EnrichedVideoData | null> => {
  const id = String(talkId);
  const cacheKey = `${id}-${language}`;

  // Check cache first
  if (videoDataCache.has(cacheKey)) {
    return videoDataCache.get(cacheKey) || null;
  }

  try {
    const { data } = await apolloClient.query({
      query: VideoSlugDocument,
      variables: {
        id: [Number(id)],
        language,
        isPublished: [true]
      },
      fetchPolicy: 'cache-first'
    });

    const video = data?.videos?.nodes?.[0];

    if (video) {
      const enrichedData: EnrichedVideoData = {
        id: String(video.id),
        slug: video.slug,
        title: video.title,
        description: video.description,
        presenterDisplayName: video.presenterDisplayName,
        duration: video.duration,
        viewedCount: video.viewedCount,
        publishedAt: video.publishedAt,
        topics: video.topics,
        primaryImageSet: video.primaryImageSet
      };

      // Cache the result
      videoDataCache.set(cacheKey, enrichedData);
      return enrichedData;
    }

    log.warn(`No video data found for talk ID: ${id}`);
    return null;
  } catch (error) {
    log.error(`Error fetching video data for talk ID ${id}:`, error);
    return null;
  }
};

/**
 * Gets the correct talk slug from GraphQL using the talk ID from Sailthru.
 * This is a convenience function that returns just the slug.
 *
 * @param talkId - The talk ID returned from Sailthru
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<string | null> - The correct slug or null if not found
 */
export const getTalkSlug = async (
  talkId: string | number,
  language = 'en'
): Promise<string | null> => {
  const videoData = await getEnrichedVideoData(talkId, language);
  return videoData?.slug || null;
};

/**
 * Gets multiple enriched video data objects in a single GraphQL query for better performance.
 *
 * @param talkIds - Array of talk IDs from Sailthru
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<Map<string, EnrichedVideoData>> - Map of talk ID to enriched video data
 *
 * @example
 * ```ts
 * const talkIds = ['12345', '67890'];
 * const videoDataMap = await getEnrichedVideoDataBatch(talkIds);
 * const firstVideoData = videoDataMap.get('12345');
 * ```
 */
export const getEnrichedVideoDataBatch = async (
  talkIds: (string | number)[],
  language = 'en'
): Promise<Map<string, EnrichedVideoData>> => {
  const resultMap = new Map<string, EnrichedVideoData>();
  const uncachedIds: number[] = [];

  // Check cache first and collect uncached IDs
  for (const talkId of talkIds) {
    const id = String(talkId);
    const cacheKey = `${id}-${language}`;

    if (videoDataCache.has(cacheKey)) {
      const cachedData = videoDataCache.get(cacheKey);
      if (cachedData) {
        resultMap.set(id, cachedData);
      }
    } else {
      uncachedIds.push(Number(id));
    }
  }

  // Fetch uncached video data
  if (uncachedIds.length > 0) {
    try {
      const { data } = await apolloClient.query({
        query: VideoSlugDocument,
        variables: {
          id: uncachedIds,
          language,
          isPublished: [true],
          first: uncachedIds.length
        },
        fetchPolicy: 'cache-first'
      });

      const videos = data?.videos?.nodes || [];

      for (const video of videos) {
        if (video?.id) {
          const id = String(video.id);
          const cacheKey = `${id}-${language}`;

          const enrichedData: EnrichedVideoData = {
            id,
            slug: video.slug,
            title: video.title,
            description: video.description,
            presenterDisplayName: video.presenterDisplayName,
            duration: video.duration,
            viewedCount: video.viewedCount,
            publishedAt: video.publishedAt,
            topics: video.topics,
            primaryImageSet: video.primaryImageSet
          };

          // Cache and add to result
          videoDataCache.set(cacheKey, enrichedData);
          resultMap.set(id, enrichedData);
        }
      }
    } catch (error) {
      log.error(
        `Error fetching video data for talk IDs ${uncachedIds.join(', ')}:`,
        error
      );
    }
  }

  return resultMap;
};

/**
 * Gets multiple talk slugs in a single GraphQL query for better performance.
 * This is a convenience function that returns just the slugs.
 *
 * @param talkIds - Array of talk IDs from Sailthru
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<Map<string, string>> - Map of talk ID to slug
 */
export const getTalkSlugs = async (
  talkIds: (string | number)[],
  language = 'en'
): Promise<Map<string, string>> => {
  const videoDataMap = await getEnrichedVideoDataBatch(talkIds, language);
  const slugMap = new Map<string, string>();

  for (const [id, videoData] of videoDataMap.entries()) {
    if (videoData.slug) {
      slugMap.set(id, videoData.slug);
    }
  }

  return slugMap;
};

/**
 * Updates a SailthruFormattedItem with enriched video data from GraphQL.
 *
 * @param item - The SailthruFormattedItem to update
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<SailthruFormattedItem> - The item with enriched data from GraphQL
 *
 * @example
 * ```ts
 * const enrichedItem = await enrichSailthruItemWithGraphQL(sailthruItem);
 * const talkUrl = `/talks/${enrichedItem.slug}`;
 * ```
 */
export const enrichSailthruItemWithGraphQL = async (
  item: { id: string; slug: string; [key: string]: any },
  language = 'en'
): Promise<typeof item> => {
  const videoData = await getEnrichedVideoData(item.id, language);

  if (!videoData) {
    return item; // Return original item if GraphQL fails
  }

  return {
    ...item,
    slug: videoData.slug || item.slug,
    title: videoData.title || item.title,
    description: videoData.description || item.description,
    presenterDisplayName:
      videoData.presenterDisplayName || item.presenterDisplayName,
    duration: videoData.duration || item.duration,
    viewedCount: videoData.viewedCount || item.viewedCount,
    publishedAt: videoData.publishedAt || item.publishedAt,
    topics: videoData.topics || item.topics,
    primaryImageSet: videoData.primaryImageSet || item.primaryImageSet
  };
};

/**
 * Updates an array of SailthruFormattedItems with enriched data from GraphQL.
 * Uses batch fetching for better performance.
 *
 * @param items - Array of SailthruFormattedItems to update
 * @param language - Optional language parameter (defaults to 'en')
 * @returns Promise<SailthruFormattedItem[]> - Array with enriched data from GraphQL
 *
 * @example
 * ```ts
 * const enrichedItems = await enrichSailthruItemsWithGraphQL(sailthruItems);
 * ```
 */
export const enrichSailthruItemsWithGraphQL = async (
  items: { id: string; slug: string; [key: string]: any }[],
  language = 'en'
): Promise<typeof items> => {
  if (items.length === 0) return items;

  const talkIds = items.map(item => item.id);
  const videoDataMap = await getEnrichedVideoDataBatch(talkIds, language);

  return items.map(item => {
    const videoData = videoDataMap.get(item.id);

    if (!videoData) {
      return item; // Return original item if no GraphQL data found
    }

    return {
      ...item,
      slug: videoData.slug || item.slug,
      title: videoData.title || item.title,
      description: videoData.description || item.description,
      presenterDisplayName:
        videoData.presenterDisplayName || item.presenterDisplayName,
      duration: videoData.duration || item.duration,
      viewedCount: videoData.viewedCount || item.viewedCount,
      publishedAt: videoData.publishedAt || item.publishedAt,
      topics: videoData.topics || item.topics,
      primaryImageSet: videoData.primaryImageSet || item.primaryImageSet
    };
  });
};

// Backward compatibility - keep old function names but mark as deprecated
/**
 * @deprecated Use enrichSailthruItemWithGraphQL instead
 */
export const correctSailthruItemSlug = enrichSailthruItemWithGraphQL;

/**
 * @deprecated Use enrichSailthruItemsWithGraphQL instead
 */
export const correctSailthruItemsSlugs = enrichSailthruItemsWithGraphQL;
